import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/NavigationBar';
import NotificationBanner from '../../components/NotificationBanner';
import Icon from '../../components/AppIcon';
import GanttChart from './components/GanttChart';
import ProjectFilters from './components/ProjectFilters';
import ProjectStatsCard from './components/ProjectStatsCard';
import MobileProjectCard from './components/MobileProjectCard';
import ProjectEditModal from './components/ProjectEditModal';
import { differenceInDays } from 'date-fns';
import { projectsService } from '../../services/crmService';

const ProjectsTimelineView = () => {
  const navigate = useNavigate();
  const today = new Date();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('delivery-date');
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  const handleRealtimeUpdate = ({ eventType, new: newData, old: oldData }) => {
    if (eventType === 'INSERT') {
      setProjects(prev => [...prev, newData]);
    } else if (eventType === 'UPDATE') {
      setProjects(prev => prev?.map(project => project?.id === newData?.id ? newData : project));
    } else if (eventType === 'DELETE') {
      setProjects(prev => prev?.filter(project => project?.id !== oldData?.id));
    }
  };

  useEffect(() => {
    loadProjects();
    const unsubscribe = projectsService?.subscribeToChanges(handleRealtimeUpdate);
    return () => unsubscribe();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await projectsService?.getAll();
      setProjects(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const getProjectStatus = (deliveryDate) => {
    const daysUntilDelivery = differenceInDays(new Date(deliveryDate), today);
    
    if (daysUntilDelivery < 0) return 'overdue';
    if (daysUntilDelivery <= 7) return 'due-soon';
    return 'on-track';
  };

  const filterProjects = () => {
    let filtered = [...projects];

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(project => 
        getProjectStatus(project?.deliveryDate) === statusFilter
      );
    }

    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'delivery-date':
          return new Date(a.deliveryDate) - new Date(b.deliveryDate);
        case 'client-name':
          return a?.clientName?.localeCompare(b?.clientName);
        case 'project-value':
          return b?.projectValue - a?.projectValue;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const calculateStats = () => {
    const onTrack = projects?.filter(p => getProjectStatus(p?.deliveryDate) === 'on-track')?.length;
    const dueSoon = projects?.filter(p => getProjectStatus(p?.deliveryDate) === 'due-soon')?.length;
    const overdue = projects?.filter(p => getProjectStatus(p?.deliveryDate) === 'overdue')?.length;
    const totalValue = projects?.reduce((sum, p) => sum + p?.projectValue, 0);

    return { onTrack, dueSoon, overdue, totalValue };
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsEditModalOpen(true);
  };

  const handleSaveProject = async (updatedProject) => {
    try {
      await projectsService?.update(updatedProject?.id, updatedProject);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to save project');
      console.error('Error saving project:', err);
    }
  };

  const filteredProjects = filterProjects();
  const stats = calculateStats();

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      {error ? (
        <NotificationBanner
          message={error}
          type="error"
          onDismiss={() => setError('')}
        />
      ) : (
        <NotificationBanner
          message="Track your project deliveries and timelines in real-time"
          type="warning"
          onDismiss={() => {}}
        />
      )}
      <main className="pt-20 px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  Projects Timeline
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Visualize and manage project deliveries with interactive Gantt chart
                </p>
              </div>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 md:px-6 py-2 md:py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth flex items-center gap-2 self-start md:self-auto"
              >
                <Icon name="LayoutDashboard" size={18} />
                <span className="text-sm md:text-base font-medium">Back to Dashboard</span>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <ProjectStatsCard
                icon="CheckCircle2"
                label="On Track"
                value={stats?.onTrack}
                color="var(--color-success)"
                bgColor="bg-green-100"
              />
              <ProjectStatsCard
                icon="Clock"
                label="Due Soon"
                value={stats?.dueSoon}
                color="var(--color-warning)"
                bgColor="bg-yellow-100"
              />
              <ProjectStatsCard
                icon="AlertCircle"
                label="Overdue"
                value={stats?.overdue}
                color="var(--color-error)"
                bgColor="bg-red-100"
              />
              <ProjectStatsCard
                icon="DollarSign"
                label="Total Value"
                value={`₹${(stats?.totalValue / 1000)?.toFixed(0)}K`}
                color="var(--color-primary)"
                bgColor="bg-purple-100"
              />
            </div>

            <ProjectFilters
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              sortBy={sortBy}
              onSortChange={setSortBy}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading projects...</p>
                </div>
              </div>
            ) : (
              <>
                {isMobileView ? (
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl shadow-elevation-2 p-4 mb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon name="Info" size={20} className="text-primary" />
                        <h3 className="text-base font-semibold text-foreground">
                          Mobile View
                        </h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Showing simplified project cards. Switch to desktop for full Gantt chart view.
                      </p>
                    </div>
                    
                    {filteredProjects?.map(project => (
                      <MobileProjectCard
                        key={project?.id}
                        project={project}
                        onEdit={handleProjectClick}
                      />
                    ))}
                  </div>
                ) : (
                  <GanttChart
                    projects={filteredProjects}
                    onProjectClick={handleProjectClick}
                    viewMode={viewMode}
                  />
                )}

                {filteredProjects?.length === 0 && (
                  <div className="bg-white rounded-xl shadow-elevation-2 p-8 md:p-12 text-center">
                    <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                      No Projects Found
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-6">
                      No projects match your current filter criteria
                    </p>
                    <button
                      onClick={() => setStatusFilter('all')}
                      className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      <ProjectEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onSave={handleSaveProject}
      />
    </div>
  );
};

export default ProjectsTimelineView;